import type { Optional } from '@festina-life/core'
import { type PropsWithChildren, Suspense, useEffect, useState } from 'react'

import { useOptionalEmployerAgreementIdContext } from '../../context/employerAgreementIdContext'
import { useFilterCommands } from '../../hooks/useFilterCommands'
import { useHook } from '../../lenteHooks'
import type { EventSelectionWithApplyDate } from '../commitmentEvents/commitmentEventSelectionContext'
import { getLatestEventSelection } from '../commitmentEvents/commitmentEventSelectionProviderLogic'
import { Provider } from './employerAgreementEventSelectionContext'

export const EmployerAgreementEventSelectionProvider = ({
  children,
}: PropsWithChildren) => {
  const [current, setCurrent] =
    useState<Optional<EventSelectionWithApplyDate>>(null)
  const tenantId = useHook('routing.tenant.useTenantParam')()
  const { employerAgreementId: employerAgreementIdFromParams } = useHook(
    'routing.employer.useEmployerAgreementParams',
  )({ isOptional: true })

  const { employerAgreementId: employerAgreementIdFromContext } =
    useOptionalEmployerAgreementIdContext()

  const employerAgreementId =
    employerAgreementIdFromContext ?? employerAgreementIdFromParams

  const { data: commands } = useHook(
    'fetchers.life.useFetchEmployerAgreementCommands',
  )(
    employerAgreementId
      ? {
          employerAgreementId,
          tenantId,
        }
      : null,
  )

  const commandAndEventData = useFilterCommands(commands)

  // Fetch and set the currentEmployerAgreementVersion whenever the current eventSelection changes.
  // We cannot let children to hit a suspense boundary above us that would cause a remount, so we wrap them in suspense.
  useEffect(() => {
    setCurrent(getLatestEventSelection(commandAndEventData))
  }, [commandAndEventData])

  return (
    <Provider value={{ current, setCurrent }}>
      <Suspense>{children}</Suspense>
    </Provider>
  )
}
